
import type { ModifiedErrorObject, FormTimeSlot, ChildFormTimeSlot } from '@/modules/TimeSlotsModule/types/time-slot-form.types'
import type { CreateTimeSlotPayload } from '@/new-services/types/time-slots'

import { defineComponent, reactive, onBeforeMount } from 'vue'
import { useRouter } from 'vue-router'

// @components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

// @module-components
import TimeSlotForm from '@/modules/TimeSlotsModule/components/TimeSlotForm/index.vue'

// @types
import type { DispatcherSector } from '@/new-services/types/dispatcher-sectors'
import type { TimeSlotZone } from '@/new-services/types/time-slot-zones'
import type { Period } from '@/new-services/types/periods'

// @new-services
import dispatcherSectorsAPI from '@/new-services/dispatcher-sectors.service'
import timeSlotZonesAPI from '@/new-services/time-slot-zones.service'
import periodsAPI from '@/new-services/periods.service'
import timeSlotsAPI from '@/new-services/time-slots.service'

// @composable
import useNotifications from '@/common/composable/useNotifications'

// @validations
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'

export default defineComponent({
    components: {
        PageTitle,
        TheButton,
        TheSelect,
        TimeSlotForm
    },
    setup() {
        const router = useRouter()
        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
            dispatcherSectorId: null as number | null,
            periodGroupId: null as number | null,
            typeId: 2,
            mainTimeSlot: {
                timeFrom: null as Date | null,
                timeTo: null as Date | null,
                isActive: true,
                zoneIds: [] as number[],
                capacities: [
                    { weekday: 0, amount: '0', errors: [] },
                    { weekday: 1, amount: '0', errors: [] },
                    { weekday: 2, amount: '0', errors: [] },
                    { weekday: 3, amount: '0', errors: [] },
                    { weekday: 4, amount: '0', errors: [] },
                    { weekday: 5, amount: '0', errors: [] },
                    { weekday: 6, amount: '0', errors: [] },
                ],
            } as FormTimeSlot,
            childTimeSlots: [] as ChildFormTimeSlot[]
        })

        const state_form$ = useVuelidate({
            dispatcherSectorId: { required },
            periodGroupId: { required }
        }, state)

        const data_state = reactive({
            dispatcher_sectors: {
                is_loading: false,
                is_failed: false,
                list: [] as DispatcherSector[]
            },
            timeSlotZones: {
                is_loading: false,
                is_failed: false,
                list: [] as TimeSlotZone[]
            },
            periods: {
                is_loading: false,
                is_failed: false,
                list: [] as Period[]
            }
        })

        const loadDispatcherSectors = async () => {
            try {
                data_state.dispatcher_sectors.is_loading = true

                const response = await dispatcherSectorsAPI.getDispatcherSectors({ page: 1, limit: 9999 })
                data_state.dispatcher_sectors.list = response.data.data

                data_state.dispatcher_sectors.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При получении списка секторов диспетчера произошла ошибка' })
                data_state.dispatcher_sectors.is_failed = true
            } finally {
                data_state.dispatcher_sectors.is_loading = false
            }
        }

        const loadTimeSlotZones = async () => {
            state_form$.value.dispatcherSectorId.$touch()
            if (state_form$.value.dispatcherSectorId.$invalid) return

            try {
                data_state.timeSlotZones.is_loading = true

                const response = await timeSlotZonesAPI.getTimeSlotZones({ page: 1, limit: 9999, dispatcherSectorIds: [state.dispatcherSectorId as number] })
                data_state.timeSlotZones.list = response.data.data

                data_state.timeSlotZones.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При получении списка секторов произошла ошибка' })
                data_state.timeSlotZones.is_failed = true
            } finally {
                data_state.timeSlotZones.is_loading = false
            }
        }

        const formatDate = (date: Date): string => date.toISOString().split('T')[0]

        const loadPeriods = async () => {
            state_form$.value.dispatcherSectorId.$touch()
            if (state_form$.value.dispatcherSectorId.$invalid) return

            try {
                data_state.periods.is_loading = true

                const currentDate = new Date(Date.now())
                const dateFrom = formatDate(currentDate)
                const dateTo = formatDate(new Date(currentDate.getFullYear() + 1, 1, 1, 12))

                const response = await periodsAPI.getPeriods({ dateFrom, dateTo, dispatcherSectorIds: [state.dispatcherSectorId as number]  })
                data_state.periods.list = response.data.data

                data_state.periods.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При получении списка периодов произошла ошибка' })
                data_state.periods.is_failed = true
            } finally {
                data_state.periods.is_loading = false
            }
        }

        onBeforeMount(loadDispatcherSectors)

        const onDispatcherSectorChanged = () => {
            loadPeriods()
            loadTimeSlotZones()

            state.mainTimeSlot.zoneIds = []
            state.childTimeSlots.forEach((_, index) => { state.childTimeSlots[index].zoneIds = [] })
        }

        const onMainTimeFrom = (timeFrom: Date | null) => state.mainTimeSlot.timeFrom = timeFrom
        const onMainTimeTo = (timeTo: Date | null) => state.mainTimeSlot.timeTo = timeTo

        const onMainTimeSlotChanged = () => {
            state.childTimeSlots.forEach((_, index) => {
                state.childTimeSlots[index].timeFrom = null
                state.childTimeSlots[index].timeTo = null
            })
        }

        const isRequired = (value: unknown): boolean => {
            if (value === null || value === undefined) return false

            if (typeof value === 'string') {
                return value.trim().length > 0
            }

            return true
        }

        const isInteger = (value: unknown): boolean => {
            if (typeof value === 'number') {
                return Number.isInteger(value)
            }

            if (typeof value === 'string' && value.trim() !== '') {
                return Number.isInteger(Number(value))
            }

            return false
        }

        const isPositive = (value: unknown): boolean => {
            if (typeof value === 'number') {
                return Number.isFinite(value) && value >= 0
            }

            if (typeof value === 'string' && value.trim() !== '') {
                const num = Number(value)
                return Number.isFinite(num) && num >= 0
            }

            return false
        }

        const isLessThenLimit = (childTimeSlotIndex: number, capacityIndex: number): boolean => {
            const capacity = state.childTimeSlots[childTimeSlotIndex].capacities[capacityIndex]

            if (capacity) {
                const limit = state.childTimeSlots[childTimeSlotIndex].limits.find((l) => l.weekday === capacity.weekday)

                if (limit) {
                    return Number(capacity.amount) <= limit.limit
                }
            }

            return false
        }

        const getErrorObject = (params: { validator: string }): ModifiedErrorObject => {
            return {
                $propertyPath: '',
                $property: '',
                $validator: params.validator,
                $message: '',
                $params: {},
                $pending: false,
                $response: null,
                $uid: ''
            }
        }

        const validateMainCapacity = (capacityIndex: number): boolean => {
            state.mainTimeSlot.capacities[capacityIndex].errors = []

            if (!isRequired(state.mainTimeSlot.capacities[capacityIndex].amount)) {
                state.mainTimeSlot.capacities[capacityIndex].errors.push(getErrorObject({ validator: 'required' }))
                return false
            }

            state.mainTimeSlot.capacities[capacityIndex].errors = []

            if (!isInteger(state.mainTimeSlot.capacities[capacityIndex].amount)) {
                state.mainTimeSlot.capacities[capacityIndex].errors.push(getErrorObject({ validator: 'integer' }))
                return false
            }

            state.mainTimeSlot.capacities[capacityIndex].errors = []

            if (!isPositive(state.mainTimeSlot.capacities[capacityIndex].amount)) {
                state.mainTimeSlot.capacities[capacityIndex].errors.push(getErrorObject({ validator: 'positive' }))
                return false
            }

            state.mainTimeSlot.capacities[capacityIndex].errors = []

            return true
        }

        const onMainCapacity = (capacity: { weekday: number; amount: string | undefined; }) => {
            const capacityIndex = state.mainTimeSlot.capacities.findIndex((c) => c.weekday === capacity.weekday)

            if (capacityIndex >= 0) {
                state.mainTimeSlot.capacities[capacityIndex].amount = capacity.amount
                validateMainCapacity(capacityIndex)
            }

            state.childTimeSlots.forEach((_, childTimeSlotI) => {
                calculateLimitsForChildTimeSlot(childTimeSlotI)
            })
        }

        const onMainSelectTimeSlotZone = (timeSlotZoneId: number) => {
            state.mainTimeSlot.zoneIds.push(timeSlotZoneId)
        }

        const onMainUnselectTimeSlotZone = (timeSlotZoneId: number) => {
            const index = state.mainTimeSlot.zoneIds.findIndex((id) => id === timeSlotZoneId)

            if (index >= 0) {
                state.mainTimeSlot.zoneIds.splice(index, 1)

                state.childTimeSlots.forEach((childTimeSlot) => {
                    const sectorIndex = childTimeSlot.zoneIds.findIndex((id) => id === timeSlotZoneId)

                    if (sectorIndex >= 0) {
                        childTimeSlot.zoneIds.splice(sectorIndex, 1)
                    }
                })
            }
        }

        const onMainToggleActive = () => {
            state.mainTimeSlot.isActive = !state.mainTimeSlot.isActive
        }

        const calculateLimitsForChildTimeSlot = (childTimeSlotIndex: number) => {
            if (state.childTimeSlots[childTimeSlotIndex]) {
                if (childTimeSlotIndex === 0) {
                    state.childTimeSlots[childTimeSlotIndex].limits.forEach((item, limitIndex) => {
                        const capacityIndex = state.mainTimeSlot.capacities.findIndex((c) => c.weekday === item.weekday)

                        if (capacityIndex >= 0) {
                            state.childTimeSlots[childTimeSlotIndex].limits[limitIndex].limit = Number(state.mainTimeSlot.capacities[capacityIndex].amount)
                        }
                    })
                } else {
                    state.childTimeSlots[childTimeSlotIndex].limits.forEach((item, limitIndex) => {
                        const capacityIndex = state.childTimeSlots[childTimeSlotIndex - 1].capacities.findIndex((c) => c.weekday === item.weekday)

                        if (capacityIndex >= 0) {
                            state.childTimeSlots[childTimeSlotIndex].limits[limitIndex].limit = state.childTimeSlots[childTimeSlotIndex - 1].limits[capacityIndex].limit - Number(state.childTimeSlots[childTimeSlotIndex - 1].capacities[capacityIndex].amount)
                        }
                    })
                }
            }
        }

        const isAllChildSlotsValid = (): boolean => {
            let valid = true

            state.childTimeSlots.forEach((childTimeSlot, index) => {
                if (!childTimeSlot.timeFrom || !childTimeSlot.timeTo) {
                    notifications.notify({ type: 'danger', title: 'Укажите временной период', description: `Не указан временной период для внутреннего тайм-слота №${index + 1}` })
                    valid = false
                }

                if (childTimeSlot.capacities.some((_, i) => !validateChildCapacity(index, i))) {
                    notifications.notify({ type: 'danger', title: 'Ошибка валидации доступных ресурсов', description: `Пожалуйста, заполните все поля корректно для внутреннего тайм-слота №${index + 1}` })
                    valid = false
                }

                if (childTimeSlot.zoneIds.length === 0) {
                    notifications.notify({ type: 'danger', title: 'Укажите сектора доступности', description: `Не выбран сектор доступности для внутреннего тайм-слота №${index + 1}` })
                    valid = false
                }
            })

            return valid
        }

        const onAddChildTimeSlot = () => {
            if (state.is_loading) return

            if (!state.mainTimeSlot.timeFrom || !state.mainTimeSlot.timeTo) {
                notifications.notify({ type: 'danger', title: 'Укажите временной период', description: 'Не указан временной период' })
                return
            }

            if (state.mainTimeSlot.capacities.some((_, i) => !validateMainCapacity(i))) {
                notifications.notify({ type: 'danger', title: 'Ошибка валидации доступных ресурсов', description: 'Пожалуйста, заполните все поля корректно' })
                return
            }

            if (state.mainTimeSlot.zoneIds.length === 0) {
                notifications.notify({ type: 'danger', title: 'Укажите сектора доступности', description: 'Не выбран сектор доступности' })
                return
            }

            if (isAllChildSlotsValid()) {
                state.childTimeSlots.push({
                    timeFrom: null as Date | null,
                    timeTo: null as Date | null,
                    isActive: true,
                    zoneIds: [] as number[],
                    capacities: [
                        { weekday: 0, amount: '0', errors: [] },
                        { weekday: 1, amount: '0', errors: [] },
                        { weekday: 2, amount: '0', errors: [] },
                        { weekday: 3, amount: '0', errors: [] },
                        { weekday: 4, amount: '0', errors: [] },
                        { weekday: 5, amount: '0', errors: [] },
                        { weekday: 6, amount: '0', errors: [] },
                    ],
                    limits: [
                        { weekday: 0, limit: 0 },
                        { weekday: 1, limit: 0 },
                        { weekday: 2, limit: 0 },
                        { weekday: 3, limit: 0 },
                        { weekday: 4, limit: 0 },
                        { weekday: 5, limit: 0 },
                        { weekday: 6, limit: 0 },
                    ]
                })

                calculateLimitsForChildTimeSlot(state.childTimeSlots.length - 1)
            }
        }

        const onChildTimeFrom = (childTimeSlotIndex: number, timeFrom: Date | null) => state.childTimeSlots[childTimeSlotIndex].timeFrom = timeFrom
        const onChildTimeTo = (childTimeSlotIndex: number, timeTo: Date | null) => state.childTimeSlots[childTimeSlotIndex].timeTo = timeTo

        const validateChildCapacity = (childTimeSlotIndex: number, capacityIndex: number): boolean => {
            state.childTimeSlots[childTimeSlotIndex].capacities[capacityIndex].errors = []

            if (!isRequired(state.childTimeSlots[childTimeSlotIndex].capacities[capacityIndex].amount)) {
                state.childTimeSlots[childTimeSlotIndex].capacities[capacityIndex].errors.push(getErrorObject({ validator: 'required' }))
                return false
            }

            state.childTimeSlots[childTimeSlotIndex].capacities[capacityIndex].errors = []

            if (!isInteger(state.childTimeSlots[childTimeSlotIndex].capacities[capacityIndex].amount)) {
                state.childTimeSlots[childTimeSlotIndex].capacities[capacityIndex].errors.push(getErrorObject({ validator: 'integer' }))
                return false
            }

            state.childTimeSlots[childTimeSlotIndex].capacities[capacityIndex].errors = []

            if (!isPositive(state.childTimeSlots[childTimeSlotIndex].capacities[capacityIndex].amount)) {
                state.childTimeSlots[childTimeSlotIndex].capacities[capacityIndex].errors.push(getErrorObject({ validator: 'positive' }))
                return false
            }

            state.childTimeSlots[childTimeSlotIndex].capacities[capacityIndex].errors = []

            if (!isLessThenLimit(childTimeSlotIndex, capacityIndex)) {
                state.childTimeSlots[childTimeSlotIndex].capacities[capacityIndex].errors.push(getErrorObject({ validator: 'maxValue' }))
                return false
            }

            state.childTimeSlots[childTimeSlotIndex].capacities[capacityIndex].errors = []

            return true
        }

        const onChildCapacity = (childTimeSlotIndex: number, capacity: { weekday: number; amount: string | undefined; }) => {
            const capacityIndex = state.childTimeSlots[childTimeSlotIndex].capacities.findIndex((c) => c.weekday === capacity.weekday)

            if (capacityIndex >= 0) {
                state.childTimeSlots[childTimeSlotIndex].capacities[capacityIndex].amount = capacity.amount
                validateChildCapacity(childTimeSlotIndex, capacityIndex)
            }

            state.childTimeSlots.forEach((_, childTimeSlotI) => {
                if (childTimeSlotI > childTimeSlotIndex) {
                    calculateLimitsForChildTimeSlot(childTimeSlotI)
                }
            })
        }

        const onChildSelectSector = (childTimeSlotIndex: number, timeSlotZoneId: number) => {
            state.childTimeSlots[childTimeSlotIndex].zoneIds.push(timeSlotZoneId)
        }

        const onChildUnselectSector = (childTimeSlotIndex: number, timeSlotZoneId: number) => {
            const index = state.childTimeSlots[childTimeSlotIndex].zoneIds.findIndex((id) => id === timeSlotZoneId)

            if (index >= 0) {
                state.childTimeSlots[childTimeSlotIndex].zoneIds.splice(index, 1)
            }
        }

        const onChildToggleActive = (childTimeSlotIndex: number) => {
            state.childTimeSlots[childTimeSlotIndex].isActive = !state.childTimeSlots[childTimeSlotIndex].isActive
        }

        const onDeleteChildTimeSlot = (childTimeSlotIndex: number) => {
            const answer = confirm(`Вы действительно хотите удалить Внутренний тайм-слот №${childTimeSlotIndex + 1}?`)

            if (answer) {
                state.childTimeSlots.splice(childTimeSlotIndex, 1)
            }
        }

        const getReservedTimeRangesForChildTimeSlot = (childTimeSlotIndex: number): Array<Array<Date>> => {
            const reservedTimeRanges: Array<Array<Date>> = []

            state.childTimeSlots.forEach((childTimeSlot, i) => {
                if (i !== childTimeSlotIndex) {
                    if (childTimeSlot.timeFrom && childTimeSlot.timeTo) {
                        reservedTimeRanges.push([childTimeSlot.timeFrom as Date, childTimeSlot.timeTo as Date])
                    }
                }
            })

            return reservedTimeRanges
        }

        const isMainTimeSlotValid = (): boolean => {
            let valid = true

            if (!state.mainTimeSlot.timeFrom || !state.mainTimeSlot.timeTo) {
                notifications.notify({ type: 'danger', title: 'Укажите временной период', description: 'Не указан временной период' })
                valid = false
            }

            if (state.mainTimeSlot.capacities.some((_, i) => !validateMainCapacity(i))) {
                notifications.notify({ type: 'danger', title: 'Ошибка валидации доступных ресурсов', description: 'Пожалуйста, заполните все поля корректно' })
                valid = false
            }

            if (state.mainTimeSlot.zoneIds.length === 0) {
                notifications.notify({ type: 'danger', title: 'Укажите сектора доступности', description: 'Не выбран сектор доступности' })
                valid = false
            }

            return valid
        }

        const getDateHour = (date: Date): string => String(date.getHours()).padStart(2, '0')

        const getPayload = (): CreateTimeSlotPayload => {
            return {
                dispatcherSectorId: state.dispatcherSectorId as number,
                periodGroupId: state.periodGroupId as number,
                mainTimeSlot: {
                    timeFrom: `${getDateHour(state.mainTimeSlot.timeFrom as Date)}:00`,
                    timeTo: `${getDateHour(state.mainTimeSlot.timeTo as Date)}:00`,
                    statusId: state.mainTimeSlot.isActive ? 51 : 52,
                    typeId: state.typeId,
                    zoneIds: state.mainTimeSlot.zoneIds,
                    capacities: state.mainTimeSlot.capacities.map((c) => {
                        return {
                            weekday: c.weekday,
                            amount: Number(c.amount)
                        }
                    })
                },
                childTimeSlots: state.childTimeSlots.map((childTimeSlot) => {
                    return {
                        timeFrom: `${getDateHour(childTimeSlot.timeFrom as Date)}:00`,
                        timeTo: `${getDateHour(childTimeSlot.timeTo as Date)}:00`,
                        statusId: childTimeSlot.isActive ? 51: 52,
                        typeId: state.typeId,
                        zoneIds: childTimeSlot.zoneIds,
                        capacities: childTimeSlot.capacities.map((c) => {
                            return {
                                weekday: c.weekday,
                                amount: Number(c.amount)
                            }
                        })
                    }
                })
            }
        }

        const onSubmit = async () => {
            if (state.is_loading) return

            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            if (!isMainTimeSlotValid()) return
            if (!isAllChildSlotsValid()) return

            try {
                state.is_loading = true

                const payload = getPayload()
                await timeSlotsAPI.createTimeSlot(payload)

                notifications.notify({ type: 'success', title: 'Тайм-слот добавлен' })
                router.replace('/time-slots/manage')
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При создании тайм-слота произошла ошибка' })
            } finally {
                state.is_loading = false
            }
        }

        const onCancel = () => {
            const answer = confirm('Вы действительно хотите отменить добавление тайм-слота?')
            if (!answer) return

            router.replace('/time-slots/manage')
        }

        return {
            state,
            state_form$,
            data_state,
            loadDispatcherSectors,
            loadTimeSlotZones,
            loadPeriods,
            onDispatcherSectorChanged,
            //
            onMainTimeFrom,
            onMainTimeTo,
            onMainTimeSlotChanged,
            onMainCapacity,
            onMainSelectTimeSlotZone,
            onMainUnselectTimeSlotZone,
            onMainToggleActive,
            //
            onAddChildTimeSlot,
            onDeleteChildTimeSlot,
            onChildTimeFrom,
            onChildTimeTo,
            onChildCapacity,
            onChildSelectSector,
            onChildUnselectSector,
            onChildToggleActive,
            getReservedTimeRangesForChildTimeSlot,
            //
            onSubmit,
            onCancel,
        }
    }
})
